import { z } from 'zod';

export const EXPENSE_TO_WHOM_MIN = 3;
export const EXPENSE_TO_WHOM_MAX = 200;
export const EXPENSE_REASON_MIN = 3;
export const EXPENSE_REASON_MAX = 500;
export const EXPENSE_NOTES_MIN = 3;
export const EXPENSE_NOTES_MAX = 2000;

const expenseAmount = z.coerce
  .number({ invalid_type_error: 'Enter a valid amount' })
  .refine((n) => Number.isFinite(n), { message: 'Enter a valid amount' })
  .refine((n) => n > 0, { message: 'Amount must be greater than zero' });

const expenseToWhom = z
  .string()
  .trim()
  .min(EXPENSE_TO_WHOM_MIN, `To whom must be at least ${EXPENSE_TO_WHOM_MIN} characters`)
  .max(EXPENSE_TO_WHOM_MAX, `To whom cannot exceed ${EXPENSE_TO_WHOM_MAX} characters`);

const expenseReason = z
  .string()
  .trim()
  .min(EXPENSE_REASON_MIN, `Reason must be at least ${EXPENSE_REASON_MIN} characters`)
  .max(EXPENSE_REASON_MAX, `Reason cannot exceed ${EXPENSE_REASON_MAX} characters`);

/** Optional: empty or whitespace-only OK; if any real text, enforce min/max. */
const expenseNotes = z.preprocess(
  (v) => (v == null || v === undefined ? '' : String(v)),
  z
    .string()
    .transform((s) => s.trim())
    .refine(
      (s) => s.length === 0 || (s.length >= EXPENSE_NOTES_MIN && s.length <= EXPENSE_NOTES_MAX),
      { message: `If you add notes, use ${EXPENSE_NOTES_MIN}–${EXPENSE_NOTES_MAX} characters` }
    )
);

const expenseCommon = {
  transactionDate: z.string().min(1, 'Date is required'),
  amount: expenseAmount,
  expenseToWhom,
  expenseReason,
  requestedBy: z.string().optional(),
  relatedToProject: z.enum(['yes', 'no']),
  expenseNotes,
  roleType: z.preprocess((v) => (v === '' ? null : v), z.enum(['civil', 'electrical', 'mechanical', 'not_applicable']).optional().nullable()),
  /** UI selection — maps to standardAmount / nonStandardAmount / othersAmount in the payload */
  epType: z.preprocess((v) => (v === '' ? null : v), z.enum(['standard', 'non_standard', 'others', 'not_applicable']).optional().nullable())
};

/** Record project credit (money in) modal */
export const CREDIT_RECIPIENT_MIN = 3;
export const CREDIT_RECIPIENT_MAX = 200;
export const CREDIT_REF_MAX = 120;
export const CREDIT_BANK_MAX = 100;
export const CREDIT_BRANCH_MAX = 120;
export const CREDIT_PAYMENT_TYPE_MAX = 80;
export const CREDIT_NOTES_MIN = 3;
export const CREDIT_NOTES_MAX = 2000;

export function formatInrHint(n) {
  if (!Number.isFinite(n)) return '—';
  const v = Math.round(n * 100) / 100;
  return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

const optionalCreditText = (maxLen, label) =>
  z.preprocess(
    (v) => (v == null || v === undefined ? '' : String(v)),
    z
      .string()
      .transform((s) => s.trim())
      .refine(
        (s) => s.length === 0 || (s.length >= 2 && s.length <= maxLen),
        { message: `If provided, ${label} must be 2–${maxLen} characters` }
      )
  );

const creditInternalNotes = z.preprocess(
  (v) => (v == null || v === undefined ? '' : String(v)),
  z
    .string()
    .transform((s) => s.trim())
    .refine(
      (s) => s.length === 0 || (s.length >= CREDIT_NOTES_MIN && s.length <= CREDIT_NOTES_MAX),
      { message: `If you add notes, use ${CREDIT_NOTES_MIN}–${CREDIT_NOTES_MAX} characters` }
    )
);

/**
 * @param {number} maxCredit Remaining net balance (0 = nothing left to collect).
 */
export function buildProjectCreditSchema(maxCredit) {
  return z
    .object({
      transactionDate: z.string().min(1, 'Date is required'),
      amount: z.coerce.number({ invalid_type_error: 'Enter a valid amount' }),
      paymentMethod: z.enum(['bank_transfer', 'upi_qr', 'cash', 'cheque']),
      paymentType: z
        .string()
        .trim()
        .min(1, 'Select payment type')
        .max(CREDIT_PAYMENT_TYPE_MAX, `Payment type cannot exceed ${CREDIT_PAYMENT_TYPE_MAX} characters`),
      refId: optionalCreditText(CREDIT_REF_MAX, 'reference ID'),
      bankName: optionalCreditText(CREDIT_BANK_MAX, 'bank name'),
      branch: optionalCreditText(CREDIT_BRANCH_MAX, 'branch'),
      recipient: z
        .string()
        .trim()
        .min(CREDIT_RECIPIENT_MIN, `Recipient must be at least ${CREDIT_RECIPIENT_MIN} characters`)
        .max(CREDIT_RECIPIENT_MAX, `Recipient cannot exceed ${CREDIT_RECIPIENT_MAX} characters`),
      internalNotes: creditInternalNotes,
      // Dynamic fields based on payment method
      upiPhone: z.string().optional(),
      receiverName: z.string().optional(),
      receiverPhone: z.string().optional(),
      chequeNumber: z.string().optional(),
      chequeDate: z.string().optional()
    })
    .superRefine((data, ctx) => {
      const n = Number(data.amount);
      if (!Number.isFinite(n)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter a valid amount', path: ['amount'] });
      } else if (n <= 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Amount must be greater than zero', path: ['amount'] });
      }

      // Conditional Required Fields based on paymentMethod
      if (data.paymentMethod === 'upi_qr') {
        if (!data.upiPhone || data.upiPhone.trim().length !== 10) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Valid 10-digit UPI phone is required', path: ['upiPhone'] });
        }
      }

      if (data.paymentMethod === 'cash') {
        if (!data.receiverName || data.receiverName.trim().length < 3) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Receiver name is required (min 3 chars)', path: ['receiverName'] });
        }
        if (!data.receiverPhone || data.receiverPhone.trim().length !== 10) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Valid 10-digit receiver phone is required', path: ['receiverPhone'] });
        }
      }

      if (data.paymentMethod === 'cheque') {
        if (!data.chequeNumber || data.chequeNumber.trim().length !== 6) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Valid 6-digit cheque number is required', path: ['chequeNumber'] });
        }
        if (!data.chequeDate) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Cheque date is required', path: ['chequeDate'] });
        }
        if (!data.bankName || data.bankName.trim().length < 2) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Issuing bank is required', path: ['bankName'] });
        }
      }
    });
}

export const expenseRequestSchema = z.object(expenseCommon);

/** General / tower expense screen: same as project modal + project pick when yes */
export const towerExpenseFormSchema = z
  .object({
    ...expenseCommon,
    projectId: z.string().optional()
  })
  .superRefine((data, ctx) => {
    if (data.relatedToProject === 'yes' && (!data.projectId || data.projectId === '')) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Select a project', path: ['projectId'] });
    }
  });
